import { useEffect, useRef } from 'react';
import { isCacheStale } from 'utils/cacheHelpers';

/**
 * Hook to refresh data in background when cache is stale
 * Implements "stale-while-revalidate" pattern for better UX
 *
 * @param {Function} fetchFunction - Function to fetch fresh data (should accept force parameter)
 * @param {string} cacheKey - Cache key to check staleness
 * @param {number} ttl - Time to live in milliseconds
 * @param {boolean} enabled - Whether background sync is enabled (default: true)
 *
 * @example
 * // In your component:
 * useBackgroundSync(getDashboardStats, 'dashboard_stats', CACHE_TTL.DASHBOARD_STATS);
 */
export const useBackgroundSync = (fetchFunction, cacheKey, ttl, enabled = true) => {
  const syncedRef = useRef(false);

  useEffect(() => {
    if (!enabled || syncedRef.current) return;

    // Check if cache is stale (beyond 2x TTL)
    if (isCacheStale(cacheKey, ttl)) {
      // Fetch in background (non-blocking)
      // Small delay to ensure cached data is displayed first
      setTimeout(() => {
        fetchFunction(true).catch(() => {
          // Background sync failed silently
        });
      }, 100);

      syncedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, ttl, enabled]);
  // Note: fetchFunction excluded from deps to avoid re-running on every render
  // This is intentional as we only want to sync once per mount when stale
};

/**
 * Hook to prefetch data before it's needed
 * Useful for anticipated navigation or user actions
 *
 * @param {Function} fetchFunction - Function to fetch data
 * @param {boolean} shouldPrefetch - Condition to trigger prefetch
 * @param {number} delay - Delay before prefetching (ms, default: 0)
 *
 * @example
 * // Prefetch when user hovers over a link
 * const [hovering, setHovering] = useState(false);
 * usePrefetch(getProjectDetails, hovering, 200);
 */ export const usePrefetch = (fetchFunction, shouldPrefetch, delay = 0) => {
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (!shouldPrefetch || prefetchedRef.current) return () => {};

    const timer = setTimeout(() => {
      fetchFunction().catch(() => {
        // Prefetch failed silently
      });
      prefetchedRef.current = true;
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPrefetch, delay]);
  // Note: fetchFunction excluded from deps intentionally to avoid re-prefetch
};
export default useBackgroundSync;
